import * as types from "_graphql-types-frontend/graphql";
import { gql } from "_graphql-types-frontend";
import { dynamicgql } from "../../../utils/graphql";
import { capitalize } from "lodash";
import { NO_QUERY } from "./store";
import { TemplateConfig } from "./config";
import { generateTemplateFragmentKey } from "../Template.helpers";
import { FieldInput, TemplatePeriod } from "../types";
import {
  FIELD_VALUE_SELECTION,
  fieldValuesToMap,
  toFieldValueInputs,
  genericFieldsMutationField,
} from "./configHelpers";
import uuid from "../../../utils/uuid";

export const FETCH_INVESTMENT_TEMPLATE = gql(`
  query FETCH_INVESTMENT_TEMPLATE(
    $id: Int!
    $name: FieldSetType!
    $version: String
    $state: String
  ) {
    investment(id: $id) {
      id
      firmId
      publishedFieldState
      templateOptions(fieldSetType: $name)
      fieldData(fieldSetType: $name, version: $version, state: $state) {
        assetClassName
        fieldMeta
        fieldSet
        fieldSetType
        strategyName
        version
      }
    }
  }
`);

function getInvestmentFieldsQuery(operation: "deleteFields" | "updateFields") {
  return (
    fields: Record<string, { [key: string]: FieldInput }>,
    data: types.Fetch_Investment_TemplateQuery,
    period?: TemplatePeriod
  ) => {
    if (!data || !data.investment || !data.investment.fieldData) return null;

    // When a reporting period is active (ongoing-reporting tab), stamp every
    // investment field input with the period anchor so the write is scoped to it.
    const investmentFields = period
      ? Object.fromEntries(
          Object.entries(fields.investment ?? {}).map(([key, field]) => [
            key,
            {
              ...field,
              asOfDate: period.asOfDate,
              periodType: period.periodType,
            },
          ])
        )
      : fields.investment;

    const queryId = generateTemplateFragmentKey(operation);

    // Generic write: [FieldValueInput] per owner into updateX/deleteXFieldValues,
    // returning `values` so the mutation result updates the same normalized
    // InvestmentFields.values the generic read observes (cache-coherent edit).
    const investmentValues = toFieldValueInputs(investmentFields);
    const firmValues = toFieldValueInputs(fields.firm);
    if (!investmentValues && !firmValues) return null;
    const genericInvestmentId = data.investment.id;
    const genericFirmId = data.investment.firmId;
    const params = [
      "$state: String",
      investmentValues
        ? "$investmentId: Int!, $investmentValues: [FieldValueInput!]!"
        : "",
      firmValues ? "$firmId: Int!, $firmValues: [FieldValueInput!]!" : "",
    ]
      .filter(Boolean)
      .join(", ");
    return {
      query: dynamicgql`
        mutation ${queryId}(${params}) {
          ${
            investmentValues
              ? genericFieldsMutationField(
                  operation,
                  "investment",
                  "investmentId",
                  "investmentValues"
                )
              : ""
          }
          ${
            firmValues
              ? genericFieldsMutationField(
                  operation,
                  "firm",
                  "firmId",
                  "firmValues"
                )
              : ""
          }
        }
      `,
      variables: ({ state }: { state: string | null }) => ({
        state,
        ...(investmentValues
          ? { investmentId: genericInvestmentId, investmentValues }
          : {}),
        ...(firmValues ? { firmId: genericFirmId, firmValues } : {}),
      }),
    };
  };
}

const saveFieldsQuery = getInvestmentFieldsQuery("updateFields");
const deleteFieldsQuery = getInvestmentFieldsQuery("deleteFields");

export const investmentTemplateConfig: TemplateConfig = {
  templateQuery: FETCH_INVESTMENT_TEMPLATE,
  getTemplateData(data: types.Fetch_Investment_TemplateQuery) {
    return data.investment.fieldData;
  },
  getPublishedFieldState(data: types.Fetch_Investment_TemplateQuery) {
    return data?.investment?.publishedFieldState;
  },
  getFieldsData(data: {
    investment: {
      fields: types.InvestmentFields;
    };
    firm: {
      fields: types.FirmFields;
    };
  }) {
    return {
      // #4889: with the generic read, values come as a [FieldValue] array; the
      // section components (Pie/CheckList/ListTable/ConditionalDescription) read
      // publishedData[owner][fieldKey], so convert the array to a fieldKey map.
      investment: fieldValuesToMap(data.investment.fields),
      firm: fieldValuesToMap(data.firm.fields),
    };
  },
  getTemplateDataQuery(
    name: string,
    data: types.Fetch_Investment_TemplateQuery,
    period?: TemplatePeriod
  ) {
    if (!data || !data.investment || !data.investment.fieldData)
      return {
        query: NO_QUERY,
        variables: () => ({}),
      };

    // When a reporting period is active, scope the investment field VALUES to it
    // (firm fields are not period-scoped). Omitted entirely otherwise so the
    // standard tabs issue the exact same query as before.
    const periodParams = period ? ", $asOfDate: DateTime, $periodType: String" : "";
    const periodArgs = period ? ", asOfDate: $asOfDate, periodType: $periodType" : "";

    return {
      query: dynamicgql`
        query TemplateFields${name}(
          $firmId: Int!, $investmentId: Int!, $state: String${periodParams}
        ) {
          investment(id: $investmentId) {
            id
            fields(state: $state${periodArgs}) {
              investmentId
              state
              ${FIELD_VALUE_SELECTION}
            }
          }
          firm(id: $firmId) {
            id
            fields(state: $state) {
              firmId
              state
              ${FIELD_VALUE_SELECTION}
            }
          }
        }`,
      variables: ({ state }: { state: string | null }) => ({
        investmentId: data.investment.id,
        firmId: data.investment.firmId,
        state,
        ...(period
          ? { asOfDate: period.asOfDate, periodType: period.periodType }
          : {}),
      }),
    };
  },
  getEntityOwnerIds(data: types.Fetch_Investment_TemplateQuery) {
    if (!data) return null;
    if (!data.investment.firmId)
      throw new Error("Expected firm id for template investment.");
    return {
      firm: { id: data.investment.firmId, fieldKey: "firmId" },
      investment: { id: data.investment.id, fieldKey: "investmentId" },
    };
  },
  saveFieldsQuery,
  deleteFieldsQuery,
  updateTemplateQuery: (
    updateUserEmail: string,
    templateName: string,
    templateVersion: string,
    data: types.Fetch_Investment_TemplateQuery
  ) =>
    saveFieldsQuery(
      {
        investment: {
          [`investment${capitalize(templateName)}TemplateVersion`]: {
            id: uuid(),
            createDate: new Date(),
            createUser: updateUserEmail,
            ownerId: data.investment.id,
            ownerType: "investment",
            state: null,
            modifyDate: new Date(),
            modifyUser: updateUserEmail,
            fieldKey: `investment${capitalize(templateName)}TemplateVersion`,
            value: templateVersion,
          },
        },
      },
      data
    ),
};
